<!--
@component

For user triggered actions.

```svelte
<IconButton type="submit" variant="success" on:click={onClick} />
```
-->
<svelte:options immutable />

<script lang="ts">
import type { ClassValue } from 'svelte/elements';

import { Icon, type IconName } from '../icon';
import { preventHandler } from '../prevent-handler';
import Progress from '../progress/progress.svelte';

/** The icon shown in the button. */
export let icon: IconName;

/** aria-label text for accessibility and title tooltip */
export let label: string | undefined = undefined;

/** Whether or not the button accepts clicks. */
export let disabled = false;

/** The behavior of the button. */
export let type: 'button' | 'submit' | 'reset' = 'button';

/** The communicated action type to the user. */
export let variant:
  | 'primary'
  | 'secondary'
  | 'ghost-outline'
  | 'danger'
  | 'danger-filled'
  | 'dark'
  | 'ghost' = 'primary';

/** The size of the button. */
export let size: 'medium' | 'large' = 'medium';

/** Shows progress indicator. Determinite progress is a @TODO */
export let progress: 'indeterminate' | number | undefined = undefined;

/** Additional CSS classes to pass to the button. */
let extraClasses: ClassValue = '';
export { extraClasses as cx };

$: handleDisabled = preventHandler(disabled);
</script>

<button
  {type}
  aria-label={label}
  aria-disabled={disabled ? true : undefined}
  class={[
    'inline-flex items-center justify-center whitespace-nowrap select-none',
    size === 'large' ? 'size-[34px]' : 'size-7.5',
    {
      'text-gray-7': size === 'large' && !disabled,
      'text-gray-6': size !== 'large' && !disabled,
      'text-gray-4 cursor-not-allowed': disabled,
      'hover:border-medium hover:bg-medium hover:text-gray-7 active:bg-gray-2 active:text-gray-8':
        variant === 'primary' && !disabled,
      'text-default hover:bg-ghost-light active:border-ghost-medium active:bg-ghost-medium border border-transparent':
        variant === 'ghost' && !disabled,
      'border-light bg-light hover:border-medium hover:bg-medium active:border-medium active:bg-gray-2 border':
        variant === 'secondary' && !disabled,
      'border-light hover:border-medium hover:bg-ghost-light hover:text-gray-8 active:border-medium active:bg-gray-2 border':
        variant === 'ghost-outline' && !disabled,
      'hover:bg-danger-dark/10 hover:text-danger-dark active:text-danger-dark active:bg-[rgba(190,53,54,0.16)]':
        variant === 'danger' && !disabled,
      'border-danger-medium bg-danger-light !text-danger-dark border hover:bg-[#F6C7C1] active:bg-[#F0A398]':
        variant === 'danger-filled' && !disabled,
      'border-gray-9 bg-gray-9 text-white hover:border-black hover:bg-black active:bg-black':
        variant === 'dark' && !disabled,
    },
    extraClasses,
  ]}
  title={$$restProps['aria-describedby'] ? undefined : label}
  {...$$restProps}
  on:click
  on:click|capture={handleDisabled}
>
  {#if progress}
    <Progress variant={variant === 'danger' ? 'light' : 'dark'} />
  {:else}
    <Icon name={icon} />
  {/if}
</button>
